import * as S from './styled';
import React, { useEffect, useState, Fragment } from 'react';
import { useAppSelector } from '../../../../../../../redux/store';
import {
  addProductTable,
  addProductTableEdges,
  addProductTableExtras,
  addProductTableComments,
} from '../../../../../../../redux/features/product/product-slice';
import CustomLoader from '../../../../../../data/customLoader';
import { IActivedTab } from '../../index';
import { notifyError, notifyWarning, notify } from '../../../../../../notify/notify-component';
import api from '../../../../../../../Services/api';
import TreeImport from './TreeImport';
import Globals from '../../../../../../../Globals.json';

interface IProps {
  show: boolean;
  handleClose: () => void;
  dispatch?: any;
  storeId?: number;
  activedtab: IActivedTab;
  categories?: any;
}

export function ModalFamily({
  show,
  handleClose,
  dispatch,
  storeId,
  activedtab,
}: IProps) {
  const { flavors, edges, extras, comments } = useAppSelector(
    ({ productReducer }) => productReducer.value
  );
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Buscar categorias em árvore quando o modal abrir
  useEffect(() => {
    const fetchTreeCategories = async () => {
      if (!show || !storeId) return;

      setLoading(true);
      try {
        const response = await api.get(`${Globals.api.treeCategories}`, {
          params: {
            page: 1,
            paginate: false,
            store_id: storeId,
          }
        });

        const data = response?.data?.data ?? [];
        setTreeData(data);
      } catch (error) {
        console.error('Erro ao buscar categorias em árvore:', error);
        notifyError('Erro ao carregar categorias');
        setTreeData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTreeCategories();
  }, [show, storeId]);

  // Função para selecionar/desselecionar categorias
  const handleToggleCategory = (category: any) => {
    setSelectedCategories(prev => {
      const isSelected = prev.some(cat => cat.id === category.id);
      if (isSelected) {
        return prev.filter(cat => cat.id !== category.id);
      } else {
        return [...prev, category];
      }
    });
  };

  // Verificar se uma categoria está selecionada
  const isCategorySelected = (categoryId: number) => {
    return selectedCategories.some(cat => cat.id === categoryId);
  };

  // Função para buscar produtos de múltiplas categorias e importá-los
  const handleSelectCategories = async () => {
    if (selectedCategories.length === 0) {
      notifyWarning('Nenhuma categoria selecionada!');
      return;
    }

    // Verificar se todas as categorias têm id_pdv_category
    const categoriesWithoutPdvId = selectedCategories.filter(cat => !cat.id_pdv_category);
    if (categoriesWithoutPdvId.length > 0) {
      notifyError(`${categoriesWithoutPdvId.length} categoria(s) sem ID PDV associado`);
      return;
    }

    setImportLoading(true);
    
    try {
      let totalImportedCount = 0;
      let totalDuplicateCount = 0;
      let totalProductsFound = 0;

      // Verificar qual lista usar baseado na aba ativa
      let existingList: any[] = [];
      let typeValue = 1;

      if (activedtab.sabores) {
        existingList = [...flavors];
        typeValue = 1;
      } else if (activedtab.borda) {
        existingList = [...edges];
        typeValue = 2;
      } else if (activedtab.extra) {
        existingList = [...extras];
        typeValue = 3;
      } else if (activedtab.comentario) {
        existingList = [...comments];
        typeValue = 4;
      }

      // Processar cada categoria selecionada
      for (const category of selectedCategories) {
        try {
          // Buscar produtos da categoria usando a instância api configurada
          const response = await api.get('/api/v1/store-products/products', {
            params: {
              'filter[id_pdv_category]': category.id_pdv_category,
              'store_id': storeId,
              'filter[is_active]': true,
              'paginate': false
            }
          });
          
          const products = response?.data?.data ?? [];
          totalProductsFound += products.length;

          // Filtrar produtos que já não estão na lista
          products.forEach((product: any) => {
            const hasProduct = existingList.find(
              (item: any) => item?.ItemKeyId === product?.id_pdv?.toString()
            );

            if (!hasProduct) {
              const newProduct = {
                ...product,
                ItemKeyId: product?.id_pdv?.toString(),
                ItemDescription: product.sale_name,
                Description: product.sale_name,
                Price: product?.sale_price,
                sale_price: product?.sale_price,
                Type: typeValue,
              };
              
              // Despachar a ação correta baseada no tipo
              if (activedtab.sabores) {
                dispatch(addProductTable(newProduct));
              } else if (activedtab.borda) {
                dispatch(addProductTableEdges(newProduct));
              } else if (activedtab.extra) {
                dispatch(addProductTableExtras(newProduct));
              } else if (activedtab.comentario) {
                dispatch(addProductTableComments(newProduct));
              }
              
              totalImportedCount++;
              existingList.push(newProduct);
            } else {
              totalDuplicateCount++;
            }
          });
        } catch (error: any) {
          console.error(`Erro ao importar produtos da categoria ${category.name}:`, error);
        }
      }

      // Notificar resultado consolidado
      if (totalProductsFound === 0) {
        notifyError('Nenhum produto encontrado nas categorias selecionadas');
      } else if (totalImportedCount > 0 && totalDuplicateCount > 0) {
        notify(`${totalImportedCount} produto(s) importado(s). ${totalDuplicateCount} já existiam.`);
      } else if (totalImportedCount > 0) {
        notify(`${totalImportedCount} produto(s) importado(s) com sucesso!`);
      } else if (totalDuplicateCount > 0) {
        notifyError(`Todos os ${totalDuplicateCount} produto(s) já estão cadastrados`);
      }

      setSelectedCategories([]);
      handleClose();
    } catch (error: any) {
      console.error('Erro ao importar produtos:', error);
      
      if (error?.response?.status === 401) {
        notifyError('Sessão expirada. Faça login novamente.');
      } else if (error?.response?.data?.message) {
        notifyError(error.response.data.message);
      } else {
        notifyError('Erro ao importar produtos das categorias');
      }
    } finally {
      setImportLoading(false);
    }
  };

  const handleCancelAdd = () => {
    setSelectedCategories([]);
    handleClose();
  };

  useEffect(() => {
    if (!show) {
      setSelectedCategories([]);
    }
  }, [show]);

  return (
    <S.ModalFamily show={show} onHide={handleCancelAdd} centered size="lg">
      <S.Container>
        <h2>Importar Família...</h2>
        
        {selectedCategories.length > 0 && (
          <div style={{ 
            padding: '0.5rem 1rem', 
            fontWeight: 'bold', 
            color: '#007bff',
            backgroundColor: '#e7f3ff',
            borderRadius: '6px',
            marginBottom: '1rem'
          }}>
            {selectedCategories.length} categoria(s) selecionada(s)
          </div>
        )}
        
        <div style={{ 
          padding: '1rem 0', 
          overflowY: 'auto', 
          maxHeight: '500px',
          minHeight: '400px'
        }}>
          {loading || importLoading ? (
            <CustomLoader />
          ) : treeData.length > 0 ? (
            <div className="tree-container">
              {treeData.map((tree: any) => (
            <Fragment key={tree.id}>
              <TreeImport
                category={tree}
                onToggleCategory={handleToggleCategory}
                isSelected={isCategorySelected(tree.id)}
                disabled={importLoading}
                // ADICIONE esta linha:
                isCategorySelected={isCategorySelected}
              />
            </Fragment>
          ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '2rem', 
              color: '#999' 
            }}>
              Nenhuma categoria foi encontrada.
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '12px', 
          padding: '16px 0',
          borderTop: '1px solid #e0e0e0',
          marginTop: '16px'
        }}>
          <button
            onClick={handleCancelAdd}
            disabled={importLoading}
            style={{
              padding: '10px 24px',
              backgroundColor: '#6c757d',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: importLoading ? 'not-allowed' : 'pointer',
              opacity: importLoading ? 0.6 : 1,
              transition: 'all 0.2s ease',
              minWidth: '100px'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSelectCategories}
            disabled={importLoading || selectedCategories.length === 0}
            style={{
              padding: '10px 24px',
              backgroundColor: selectedCategories.length === 0 || importLoading ? '#ccc' : '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: selectedCategories.length === 0 || importLoading ? 'not-allowed' : 'pointer',
              opacity: selectedCategories.length === 0 || importLoading ? 0.6 : 1,
              transition: 'all 0.2s ease',
              minWidth: '100px'
            }}
          >
            {importLoading ? 'Importando...' : 'Ok'}
          </button>
        </div>
      </S.Container>
    </S.ModalFamily>
  );
}